import { useEffect } from "react";
import { Controller, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { toast } from "sonner";

import { CCard, CCardBody, CCardHeader } from "@coreui/react";

import { khoTrungTamApi } from "src/1/apis/kho_trung_tam.api";
import { CTable } from "src/1/common/components/others";
import { history } from "src/App";
import { CRange, CSelect, CTextarea } from "src/common/components/controls";
import { CActionGroup, CCardTitle } from "src/common/components/others";
import { mapColor, renderRating } from "src/modules/nha_cung_cap/funcs";
import { useGetMyStores } from "src/utils/hooks/options";
import { useTitle } from "src/utils/hooks/title";

const defaultValues = {
  storeCode: "",
  isSubmitted: false,
  evaluations: [],
};

export default function ChamDiemKTT() {
  useTitle("Chấm điểm KTT");

  //#region Data
  const params = useParams();

  const { data } = useQuery({
    queryKey: ["chi-tiet-phieu-cham-diem-ktt", params?.id],
    queryFn: () => khoTrungTamApi.getById(params.id),
    enabled: !!params.id,
    select: (res) => res?.data?.data,
  });

  const { stores } = useGetMyStores();

  const { control, handleSubmit, reset, setValue } = useForm({ defaultValues });

  const { fields, replace } = useFieldArray({
    control,
    name: "evaluations",
    keyName: "__id",
  });

  const isSubmitted = useWatch({ control, name: "isSubmitted" });
  //#endregion

  useEffect(() => {
    if (stores && stores.length && data && data.stores) {
      const storeCode = stores[0].value;
      setValue("storeCode", storeCode);
      const store = data.stores.find((e) => e.store_code === storeCode);
      setValue("isSubmitted", store.is_submitted);
      replace(
        store.scores.map((e) => ({
          criterion_code: e.criterion_code,
          label: e.criterion_label,
          score: e.score,
          note: e.note,
        })),
      );
    }
  }, [stores, data]);

  //#region Event
  const onSubmit = handleSubmit(async (values) => {
    try {
      await khoTrungTamApi.rate(params.id, values.storeCode, {
        evaluations: values.evaluations,
      });
      toast.success("Lưu thông tin đánh giá thành công");
      history.push(`/central/form/${params.id}`);
      reset(defaultValues);
    } catch (error) {
      toast.error(error?.message || "Lưu thông tin không thành công");
    }
  });

  const handleStoreChange = (_v, _o) => {
    const store = data.stores.find((e) => e.store_code === _v);
    setValue("isSubmitted", store.is_submitted);
    replace(
      store.scores.map((e) => ({
        criterion_code: e.criterion_code,
        label: e.criterion_label,
        score: e.score,
        note: e.note,
      })),
    );
  };
  //#endregion

  //#region Render
  const headers = [
    {
      key: "label",
      label: "Tiêu chí",
      width: 290,
      cellRender: (value, record, index) => (
        <div className="flex flex-col gap-1">
          <h5 className="font-semibold text-base text-left mb-0">{value}</h5>
          <Controller
            control={control}
            name={`evaluations.${index}.score`}
            render={({ field }) => (
              <CRange
                {...field}
                fillColor={mapColor(field.value)}
                disabled={isSubmitted}
              />
            )}
          />
        </div>
      ),
    },
    {
      key: "score",
      label: "Mức độ đánh giá",
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`evaluations.${index}.score`}
          render={({ field }) => renderRating(field.value)}
        />
      ),
    },
    {
      key: "note",
      label: "Ghi chú",
      cellRender: (value, record, index) => (
        <Controller
          control={control}
          name={`evaluations.${index}.note`}
          render={({ field }) => (
            <CTextarea
              placeholder="Nhập ghi chú"
              {...field}
              disabled={isSubmitted}
            />
          )}
        />
      ),
    },
  ];
  return (
    <>
      <CCard className="toolbar">
        <CCardBody>
          <CActionGroup onClick={onSubmit} canAdd={false} canSave />
        </CCardBody>
      </CCard>
      <CCard>
        <CCardHeader className="flex flex-row items-baseline justify-between">
          <div className="flex flex-col">
            <CCardTitle>
              Chi nhánh đánh giá:&nbsp;&nbsp;{data?.submitted_stores}/
              {data?.total_stores}
            </CCardTitle>
            {isSubmitted && (
              <span className="italic text-base text-red-600 font-medium">
                Chi nhánh này đã thực hiện đánh giá rồi *
              </span>
            )}
          </div>
          <Controller
            control={control}
            name="storeCode"
            render={({ field: { value, onChange } }) => (
              <CSelect
                value={value}
                onChange={onChange}
                className="min-w-[250px]"
                select="value"
                options={stores}
                onChangeCallback={handleStoreChange}
              />
            )}
          />
        </CCardHeader>

        <CCardBody>
          <CTable headers={headers} data={fields} rowKey="__id" />
        </CCardBody>
      </CCard>
    </>
  );
  //#endregion
}
